import { useCallback, useEffect, useRef, useState } from 'react'
import * as api from '../../api'
import { ApiError } from '../../api'
import { anyAttending, priceOf } from '../../rooms'
import type { Invite, RoomType } from '../../types'
import { weddingInfo } from '../../weddingInfo'
import { SectionHeading } from '../SectionHeading'
import { RoomChoices } from './RoomChoices'
import { RoomConfirmation } from './RoomConfirmation'

interface RoomSectionProps {
  invite: Invite
  onSaved: (invite: Invite) => void
  onSessionLost: () => void
}

/**
 * Staying the night in Horsley Towers.
 *
 * The room belongs to the whole invite rather than to a person, so this asks
 * once for the household - unlike food, which is asked of everybody separately.
 *
 * Shown to everybody, including people who have not replied and people who
 * cannot come: where guests might sleep is information about the venue, and
 * withholding it until somebody commits would be unhelpful. Only the reserving
 * is gated on actually coming.
 */
export function RoomSection({ invite, onSaved, onSessionLost }: RoomSectionProps) {
  const [types, setTypes] = useState<RoomType[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Whether the picker is open over a booking that already exists.
  const [changing, setChanging] = useState(false)
  const [busyId, setBusyId] = useState<string | null>(null)
  // True only for a reservation made just now, so the confirmation is revealed
  // rather than simply being how the page arrived.
  const [justReserved, setJustReserved] = useState(false)
  /*
   * Set when a room that had been paid for is moved or given up.
   *
   * The warning on the card is read before the click; this is what is left after
   * it, once the reservation - and with it the paid mark - has gone. Without it
   * the guest lands back on the room list with no sign that their money is now
   * sitting against nothing.
   *
   * Deliberately not cleared by anything they do next: reserving a different room
   * does not un-strand the payment, so the notice stays until the page is left.
   */
  const [paymentStranded, setPaymentStranded] = useState(false)

  const attending = anyAttending(invite.people)
  const heldRoomId = invite.room?.room_type_id ?? null

  /*
   * Close the picker whenever the room actually changes.
   *
   * `changing` is remembered state describing something the data already knows:
   * a picker open over a booking. Left to the success path alone to close it,
   * the two can disagree - if that `setChanging(false)` never reaches the live
   * component, the server has the new room while the page still shows the list
   * with it marked "yours". A hot reload mid-click will do it, and so will any
   * update arriving from somewhere other than the button that was pressed.
   *
   * Reconciling against the room itself makes that state unable to persist,
   * whatever caused it: once the reservation is different from what it was, the
   * picker has nothing left to be open over.
   */
  const settledRoomId = useRef(heldRoomId)
  useEffect(() => {
    if (settledRoomId.current === heldRoomId) return
    settledRoomId.current = heldRoomId
    setChanging(false)
  }, [heldRoomId])

  const handle = useCallback(
    (err: unknown): string => {
      if (err instanceof ApiError && err.isUnauthenticated) {
        onSessionLost()
        return 'Your session expired.'
      }
      return err instanceof ApiError ? err.message : 'Something went wrong. Please try again.'
    },
    [onSessionLost],
  )

  const load = useCallback(async () => {
    try {
      setTypes(await api.getRooms())
    } catch (err) {
      setError(handle(err))
    }
  }, [handle])

  useEffect(() => {
    void load()
  }, [load])

  /*
   * Reserving changes two things at once: this invite's booking, and how many
   * rooms are left for everybody else. The response carries the first, so only
   * the counts need re-reading.
   */
  async function reserve(roomTypeId: string) {
    setBusyId(roomTypeId)
    setError(null)
    /*
     * Read before the write, and only where the money actually needs attention.
     *
     * Matches the server's rule: paid_at survives a move to a room at the same
     * price, because the sum handed over still covers it. So a Junior Suite to a
     * Family Room - both £190 - is nothing to warn about, and neither is asking
     * again for the room already held.
     *
     * A price we cannot look up counts as different. Being told to check with Tom
     * needlessly is a smaller harm than a payment quietly stranded.
     */
    const samePrice =
      priceOf(types, invite.room?.room_type_id) === priceOf(types, roomTypeId) &&
      priceOf(types, roomTypeId) !== null
    const movedAPaidRoom = invite.room !== null && invite.room.paid_at !== null && !samePrice
    try {
      const updated = await api.reserveRoom(roomTypeId)
      if (movedAPaidRoom) setPaymentStranded(true)
      onSaved(updated)
      setChanging(false)
      setJustReserved(true)
      await load()
    } catch (err) {
      setError(handle(err))
      // The count that made this fail is stale in the browser, so refresh it -
      // "fully booked" beside the room explains the refusal better than the
      // message alone.
      await load()
    } finally {
      setBusyId(null)
    }
  }

  async function release() {
    setBusyId('release')
    setError(null)
    const gaveUpAPaidRoom = invite.room !== null && invite.room.paid_at !== null
    try {
      onSaved(await api.releaseRoom())
      if (gaveUpAPaidRoom) setPaymentStranded(true)
      setChanging(false)
      setJustReserved(false)
      await load()
    } catch (err) {
      setError(handle(err))
    } finally {
      setBusyId(null)
    }
  }

  const { rooms } = weddingInfo
  const showPicker = attending && (invite.room === null || changing)
  // Only meaningful once the types have loaded; an empty list is "not known yet",
  // not "none left".
  const nothingLeft =
    types !== null && types.length > 0 && types.every((t) => t.reserved >= t.capacity)

  return (
    <section aria-labelledby="staying-over">
      <SectionHeading id="staying-over">Staying over</SectionHeading>

      <p className="mt-3 text-sm">{rooms.intro}</p>
      <p className="text-ink-soft mt-2 text-sm">{rooms.payment}</p>

      {/* Above the rooms, not below them: this decides whether the manor house is
          an option at all, so it has to be read before anything is picked.
          Boxed and labelled rather than quoted, because an indent reads as an
          aside and this is not one - but claret is kept for errors, so it takes
          the claret tint at low strength and full-contrast ink for the words. */}
      <div className="border-claret/30 bg-claret/5 mt-4 rounded-md border p-4">
        <p className="text-claret text-xs font-medium tracking-widest uppercase">Access</p>
        <p className="mt-1.5 text-sm">{rooms.access}</p>
      </div>

      {!attending && <p className="text-ink-soft mt-3 text-sm">{rooms.beforeReplying}</p>}

      {/* Somebody who is coming, holds nothing, and has nothing left to hold.
          Said once here rather than repeated on four sold-out cards. */}
      {attending && invite.room === null && nothingLeft && (
        <p className="mt-3 text-sm">{rooms.allTaken}</p>
      )}

      {error && (
        <p role="alert" className="text-claret mt-4 text-sm">
          {error}
        </p>
      )}

      {/* What is left after moving or giving up a room that was paid for. An
          alert rather than a quiet line: money they have already handed over is
          now against nothing, and only Tom can put that right. */}
      {paymentStranded && (
        <div
          role="alert"
          className="border-claret/30 bg-claret/5 mt-4 rounded-md border p-4 text-sm"
        >
          <p>{rooms.payOptions.paidAfterChange}</p>
          <p className="mt-2">
            <a
              href={`mailto:${weddingInfo.contact.email}`}
              className="text-claret underline decoration-1 underline-offset-4"
            >
              {weddingInfo.contact.email}
            </a>
            {' or '}
            <a
              href={`tel:${weddingInfo.contact.phone.replace(/\s/g, '')}`}
              className="text-claret underline decoration-1 underline-offset-4"
            >
              {weddingInfo.contact.phone}
            </a>
          </p>
        </div>
      )}

      {types === null && !error && <p className="text-ink-soft mt-4 text-sm">Loading the rooms…</p>}

      {types !== null && (
        <div className="mt-5">
          {invite.room !== null && !changing && (
            <RoomConfirmation
              reservation={invite.room}
              code={invite.code}
              types={types}
              reveal={justReserved}
              busy={busyId !== null}
              cancelling={busyId === 'release'}
              onChange={() => setChanging(true)}
              onRelease={() => void release()}
            />
          )}

          {showPicker && (
            <RoomChoices
              types={types}
              // Marks the one they already hold, so changing room reads as
              // moving rather than as booking a second.
              currentId={invite.room?.room_type_id ?? null}
              busyId={busyId}
              onReserve={(id) => void reserve(id)}
              {...(invite.room !== null ? { onKeepCurrent: () => setChanging(false) } : {})}
            />
          )}

          {/* Read-only for anybody not coming: what is on offer, without the
              buttons they cannot use. */}
          {!attending && <RoomChoices types={types} currentId={null} busyId={null} />}
        </div>
      )}

      <dl className="text-ink-soft mt-6 space-y-1 text-sm">
        <div>
          <dt className="sr-only">Check in</dt>
          <dd>{rooms.checkIn}</dd>
        </div>
        <div>
          <dt className="sr-only">Check out</dt>
          <dd>{rooms.checkOut}</dd>
        </div>
      </dl>

      <div className="border-ink/10 mt-6 border-t pt-5">
        <h3 className="font-display text-lg">{rooms.alternative.name}</h3>
        <p className="mt-1 text-sm">{rooms.alternative.body}</p>
        <a
          href={rooms.alternative.url}
          target="_blank"
          rel="noreferrer noopener"
          className="text-claret mt-2 inline-block text-sm underline decoration-1 underline-offset-4"
        >
          Book at {rooms.alternative.name}
        </a>
      </div>
    </section>
  )
}
